import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IntlContext, ListContext } from "context";
import usePromise from "helpers/usePromise";
import { ProduitInterface } from "dataProvider/Produit";
import groupDataFetcher from "dataProvider/Group";
import SearchBox from "components/List/SearchBox";
import Loader from "ui/Loader";
import { Modal } from "ui/Modal";
import { Form as ExportForm } from "../Export";
import { matomoPush } from "../helpers";

const ProduitList: React.FC = () => {
    const intl = useContext(IntlContext);
    const { changeFetcherParams, data, isLoading, activeFetch } = useContext(ListContext);
    const [filterProps /* setFilterProps */] = useState<any>({});
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    const [exportInitialValues, setExportInitialValues] = useState<HashMap<any>>({});
    const [displayModalCloseButton, setDisplayModalCloseButton] = useState<boolean>(true);
    const groupData = usePromise(groupDataFetcher.all);
    const [filterName, setFilterName] = useState<any>("");

    useEffect(() => {
        if (activeFetch) {
            const filters = data?.active_filters;
            const matomoValues = filters?.filter_name || "";
            matomoPush([["trackSiteSearch", matomoValues, "produit", data?.pager?.total]]);
        }
    }, [activeFetch, data]);

    const handleClickExport = (all: boolean = false, withFilters: boolean = true) => {
        const activeFilters = withFilters && data?.active_filters ? data?.active_filters : {};
        const filterSecteur = activeFilters?.filter_secteur ? activeFilters?.filter_secteur.split("|") : [];
        const filterFamille = activeFilters?.filter_famille ? activeFilters?.filter_famille.split("|") : [];
        setExportInitialValues(
            Object.assign({}, activeFilters, {
                filter_famille: filterFamille,
                filter_secteur: filterSecteur,
                groups: all ? ["all"] : [],
            })
        );
        setShowExportModal(true);
    };

    const handleCloseExportModal = () => {
        setShowExportModal(false);
    };

    const handleOrderChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            changeFetcherParams(
                Object.assign({}, data.active_filters, {
                    order: e.target.value,
                    direction: "asc",
                    per_page: data.pager.per_page,
                })
            );
        },
        [changeFetcherParams, data]
    );

    const totalResults = (): string => {
        const total = data?.pager?.total;
        if (typeof total !== "number") {
            return "";
        }
        if (total === 0) {
            return intl.formatMessage("produit.label.no_result");
        }
        return (
            `${total} ` +
            (total > 1 ? intl.formatMessage("produit.label.results") : intl.formatMessage("produit.label.result"))
        );
    };

    const hasResult = (): boolean => {
        return Boolean(data?.pager?.total);
    };

    const getPoidsVolume = (produit: ProduitInterface): string => {
        if (typeof produit?.poids_net_g === "number") {
            return `${produit?.poids_net_g} g`;
        } else if (typeof produit?.volume_net_mL === "number") {
            return `${produit?.volume_net_mL} mL`;
        }
        return "";
    };

    const results = () => {
        return data.map((produit: ProduitInterface, index: number) => {
            return (
                <tr className="row" key={index}>
                    <td className="cell cell--category">
                        <span>{produit.secteur?.interface?.name}</span>
                    </td>
                    <td className="cell cell--subcategory">
                        <span>{produit.famille?.interface?.name}</span>
                    </td>
                    <td className="cell cell--code">
                        <span>{produit.code_barre}</span>
                    </td>
                    <td className="cell cell--brand">
                        <span>{produit.marque_libel}</span>
                    </td>
                    <td className="cell cell--name">
                        <span>{produit.libel}</span>
                    </td>
                    <td className="cell cell--sell-name">
                        <span>{produit.denomination_vente}</span>
                    </td>
                    <td className="cell cell--weight">
                        <span>
                            {getPoidsVolume(produit)} <span className="display-block">{produit.nb_unite}</span>
                        </span>
                    </td>
                    <td className="cell cell--year">
                        <span>{produit.derniere_anneeh}</span>
                    </td>
                    <td className="cell">
                        <Link
                            to={`/produit/${produit.id}`}
                            className="icon-arrow-right"
                            onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                matomoPush([
                                    ["setReferrerUrl", window.location.href],
                                    ["setCustomUrl", `${window.location.href}produit/${produit.id}`],
                                ]);
                            }}
                        >
                            {" "}
                        </Link>
                    </td>
                </tr>
            );
        });
    };

    const goToPage = (page: number) => {
        changeFetcherParams(
            Object.assign({}, data.active_filters, {
                page,
                order: data.pager.order,
                direction: data.pager.direction,
                per_page: data.pager.per_page,
            })
        );
    };

    const paginationList = () => {
        const pages = [];

        if (data?.pager?.last_page === 1) {
            return [];
        }

        if (data?.pager?.last_page > 1 && data?.pager?.current_page > 1) {
            pages.push(
                <li key="pagination-first" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(1);
                        }}
                    >
                        <i className="icon-angle-double-left-solid"></i>
                    </Link>
                </li>
            );

            pages.push(
                <li key="pagination-prev" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(data?.pager?.current_page - 1);
                        }}
                    >
                        <i className="icon-arrow-left"></i>
                    </Link>
                </li>
            );
        }

        for (let i = 1; i <= data?.pager?.last_page; i++) {
            const minMax = 4;
            const pageMin = Math.max(
                1,
                Math.min(data?.pager?.current_page - minMax, data?.pager?.last_page - 2 * minMax)
            );
            const pageMax = Math.min(data?.pager?.last_page, pageMin + 2 * minMax);
            if (i >= pageMin && i <= pageMax) {
                pages.push(
                    <li
                        key={i}
                        className={`pagination__item ${
                            data?.pager?.current_page === i ? "pagination__item--active" : ""
                        }`}
                    >
                        <Link
                            className="pagination__item--link"
                            to="#"
                            onClick={() => {
                                goToPage(i);
                            }}
                        >
                            {i}
                        </Link>
                    </li>
                );
            }
        }

        if (data?.pager?.last_page > 1 && data?.pager?.current_page < data?.pager?.last_page) {
            pages.push(
                <li key="pagination-next" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(data?.pager?.current_page + 1);
                        }}
                    >
                        <i className="icon-arrow-right"></i>
                    </Link>
                </li>
            );

            pages.push(
                <li key="pagination-last" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(data?.pager?.last_page);
                        }}
                    >
                        <i className="icon-angle-double-right-solid"></i>
                    </Link>
                </li>
            );
        }
        return pages;
    };

    const exportLoading = (isLoading: boolean) => {
        setDisplayModalCloseButton(!isLoading);
    };

    // VUE RECHERCHE
    return (
        <div className="home-oqali">
            <header className={`header${activeFetch ? "-search" : "-home"}`}></header>

            {/* SEARCH BAR */}
            <div className="block-searchbar">
                <div className="container">
                    <SearchBox filterProps={filterProps} fnameValue={(value) => setFilterName(value)} />
                </div>
            </div>

            {activeFetch ? (
                <React.Fragment>
                    {/* SEARCH RESULTS */}
                    {/* -- order select & export button*/}
                    <div className="container container--table" style={{marginBottom: "40px"}}>
                        <Link
                            to={`/stats`}
                            className="cta--link"
                            title={intl.formatMessage("produit.link.stats")}
                            onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                    matomoPush([["trackEvent", "Visualisation des données et statistiques descriptives", "Affichage", "values", 1]]);
                            }}
                        >
                            {intl.formatMessage("produit.link.stats")}
                        </Link>
                    </div>
                    <div className="search-top-content">
                        <div className="container container--table">
                            <select name="order" className="order-by" onChange={handleOrderChange}>
                                <option value="libel">{intl.formatMessage("generic.order_by")}</option>
                                <option value="libel">{intl.formatMessage("produit.label.nom")}</option>
                                <option value="marque_libel">{intl.formatMessage("produit.label.marque")}</option>
                            </select>

                            {hasResult() && (
                                <button
                                    className="cta cta--secondary"
                                    title={intl.formatMessage("generic.export")}
                                    onClick={() => {
                                        handleClickExport(true);
                                    }}
                                >
                                    {intl.formatMessage("generic.export")}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* -- results table */}
                    {isLoading ? (
                        <div className="search-table-content">
                            <div className="container container--table">
                                <Loader message={intl.formatMessage("generic.loader.loading.label")} />
                            </div>
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className="search-table-content">
                                <div className="container container--table">
                                    <p className="search-results">{totalResults()}</p>
                                    <p className="search-warning">{intl.formatMessage("produit.label.warning")}</p>
                                    {hasResult() && (
                                        <table className="search-table">
                                            <thead className="row-group">
                                                <tr className="header-row">
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.secteur")}
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.famille")}
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.code_barre")}
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.marque")}
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.nom")}
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.denomination_vente")}
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.poids_net")} /
                                                        <span className="display-block">
                                                            {intl.formatMessage("produit.label.nb_unite_par_lot")}
                                                        </span>
                                                    </th>
                                                    <th className="header-cell">
                                                        {intl.formatMessage("produit.label.derniere_anneeh")}
                                                    </th>
                                                    <th className="header-cell"></th>
                                                </tr>
                                            </thead>

                                            <tbody className="row-group">{results()}</tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                            {/* -- pagination */}
                            <nav className="pagination">
                                <ul className="pagination__list">{paginationList()}</ul>
                            </nav>
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    {/* FAQ */}
                    <div className="page-content">
                        <div className="container">
                            <div className="home-export-link">
                                <button
                                    className="cta--link"
                                    title={intl.formatMessage("produit.action.export_all")}
                                    onClick={() => {
                                        handleClickExport(true, false);
                                    }}
                                >
                                    {intl.formatMessage("produit.action.export_all")}
                                </button>
                            </div>
                            <div className="information-cta">
                                <div className="information-cta__group">
                                    {/* <div className="information__title">{intl.formatMessage("faq.label.title")}</div> */}
                                    <p className="information__text">{intl.formatMessage("faq.label.notice")}</p>
                                </div>
                                <div className="information-cta__group information-cta__group--cta">
                                    <a
                                        href={`${process.env.REACT_APP_WP_URL}/donnees-publiques/faq/`}
                                        className="cta cta--secondary"
                                        title={intl.formatMessage("faq.action.link")}
                                    >
                                        {intl.formatMessage("faq.action.link")}
                                    </a>
                                </div>
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <Link
                                    to={`/stats`}
                                    className="cta cta--secondary"
                                    title={intl.formatMessage("produit.link.stats")}
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                                            matomoPush([["trackEvent", "Visualisation des données et statistiques descriptives", "Affichage", "values", 1]]);
                                    }}
                                >
                                    {intl.formatMessage("produit.link.stats")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            )}

            {/* -- export modal */}
            <Modal
                onClose={handleCloseExportModal}
                show={showExportModal}
                title={intl.formatMessage("generic.export")}
                closeTitle={intl.formatMessage("generic.close")}
                displayCloseButton={displayModalCloseButton}
            >
                <ExportForm
                    groups={groupData.data}
                    initialValues={exportInitialValues}
                    loading={exportLoading}
                    searchValue={filterName}
                />
            </Modal>
        </div>
    );
};

export default ProduitList;
